/**
 * Engineering permits — shared field schema, edit contexts, and grid helpers.
 */
import { repeaterToArray } from "./commercialProjectAcf";
import { fileUrlFromStoredValue } from "./CommercialFieldFileDisplay";
import { formatDateOnlyDisplay, normalizeDateOnlyIso } from "./dateOnlyDisplay";
import { PERMITTING_DRAFTER_ASSIGNMENT_ROLE_PATTERNS } from "./commercialRoleAccess";

export const PERMIT_EDIT_CONTEXT = {
  ENGINEERING: "engineering",
  PERMITTING: "permitting",
};

export const PERMIT_RIDER_TYPE_OPTIONS = [
  "Extension",
  "Route change",
  "Amendment",
  "TCP",
  "Monument survey",
  "Other",
];

export const permitRiderRowFields = [
  {
    key: "rider_type",
    label: "Type",
    type: "select",
    options: PERMIT_RIDER_TYPE_OPTIONS,
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING, PERMIT_EDIT_CONTEXT.ENGINEERING],
  },
  {
    key: "permit_plan",
    label: "Supporting document",
    type: "file",
    accept: ".pdf",
    editContexts: [PERMIT_EDIT_CONTEXT.ENGINEERING, PERMIT_EDIT_CONTEXT.PERMITTING],
  },
  {
    key: "permit_application",
    label: "Rider application",
    type: "file",
    accept: ".pdf",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING],
  },
  {
    key: "application_submitted_date",
    label: "Application submitted",
    type: "date",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING],
  },
  {
    key: "permit",
    label: "Rider permit",
    type: "file",
    accept: ".pdf",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING],
  },
  {
    key: "permit_number",
    label: "Permit number",
    type: "text",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING, PERMIT_EDIT_CONTEXT.ENGINEERING],
  },
  {
    key: "permit_received_date",
    label: "Received",
    type: "date",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING],
  },
  {
    key: "permit_expiration_date",
    label: "Expiration",
    type: "date",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING],
  },
  {
    key: "notes",
    label: "Notes",
    type: "textarea",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING],
  },
];

export const permitRowFields = [
  {
    key: "agency",
    label: "Agency",
    type: "text",
    editContexts: [PERMIT_EDIT_CONTEXT.ENGINEERING, PERMIT_EDIT_CONTEXT.PERMITTING],
  },
  {
    key: "location",
    label: "Location",
    type: "textarea",
    editContexts: [PERMIT_EDIT_CONTEXT.ENGINEERING],
  },
  {
    key: "permit_plan",
    label: "Permit plan",
    type: "file",
    accept: ".pdf",
    editContexts: [PERMIT_EDIT_CONTEXT.ENGINEERING, PERMIT_EDIT_CONTEXT.PERMITTING],
  },
  {
    key: "permit_application",
    label: "Permit application",
    type: "file",
    accept: ".pdf",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING],
  },
  {
    key: "application_submitted_date",
    label: "Application submitted",
    type: "date",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING],
  },
  {
    key: "permit",
    label: "Permit",
    type: "file",
    accept: ".pdf",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING],
  },
  {
    key: "permit_number",
    label: "Permit number",
    type: "text",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING, PERMIT_EDIT_CONTEXT.ENGINEERING],
  },
  {
    key: "permit_received_date",
    label: "Permit received",
    type: "date",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING],
  },
  {
    key: "permit_expiration_date",
    label: "Permit expiration",
    type: "date",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING],
  },
  {
    key: "refundable_deposit",
    label: "Refundable deposit",
    type: "bool",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING],
  },
  {
    key: "deposit_amount",
    label: "Deposit amount",
    type: "currency",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING],
  },
  {
    key: "materials",
    label: "Materials",
    type: "repeater",
    rowLabel: "Material",
    editContexts: [PERMIT_EDIT_CONTEXT.ENGINEERING],
    fields: [
      { key: "item", label: "Material", type: "material_catalog" },
      { key: "quantity", label: "Amount", type: "number" },
    ],
  },
  {
    key: "riders",
    label: "Supporting documents",
    type: "repeater",
    rowLabel: "Supporting document",
    addButtonLabel: "Add Supporting Documents",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING, PERMIT_EDIT_CONTEXT.ENGINEERING],
    fields: permitRiderRowFields,
  },
];

export const PERMITS_REPEATER_FIELD = {
  key: "permits",
  label: "Permits",
  type: "repeater",
  rowLabel: "Permit",
  fields: permitRowFields,
};

/** Assignment on the Permitting tab. Stored on engineering_details. */
export const PERMITTING_ASSIGNMENT_FIELDS = [
  {
    key: "drafter",
    label: "Drafter",
    type: "user_role_select",
    roleNames: ["Drafter", "Engineering Supervisor", "Engineering Manager"],
    roleTypeIds: ["role_mokc6r8s_5skrub", "role_eng_supervisor_pb01", "role_mokc6l2i_tpgjpy"],
    editRoles: PERMITTING_DRAFTER_ASSIGNMENT_ROLE_PATTERNS,
  },
  {
    key: "permitting_specialist",
    label: "Permitting Specialist",
    type: "user_role_select",
    roleNames: ["Permitting Specialist", "Permitting Specalist", "Permitting Manager"],
    roleTypeIds: ["role_permit_spec_pb01", "role_permit_mgr_pb01"],
    editRoles: PERMITTING_DRAFTER_ASSIGNMENT_ROLE_PATTERNS,
    hint: "Permit expiration and follow-up emails go to this person. Permitting Managers can be selected. If blank, they go to every Permitting Specialist, and the Permitting Manager is copied. The person selected here is emailed when the assignment changes.",
  },
];

/** Project-level permitting fields (board columns + Permitting tab). Stored on engineering_details. */
export const PERMITTING_TRACKING_FIELDS = [
  {
    key: "permit_status",
    label: "Permit Status",
    type: "textarea",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING],
  },
  {
    key: "revision_requested_date",
    label: "Revision Requested",
    type: "date",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING],
  },
  {
    key: "permit_follow_up",
    label: "Follow-up",
    type: "date",
    hint: "Sends a follow-up email on this date when the project is in Permitting.",
    editContexts: [PERMIT_EDIT_CONTEXT.PERMITTING],
  },
];

export const PERMITTING_TRACKING_FIELD_KEYS = PERMITTING_TRACKING_FIELDS.map((f) => f.key);

/** Whether permits can be added/removed in the current tab context. */
export function canManagePermitRows(editContext) {
  return (
    editContext === PERMIT_EDIT_CONTEXT.ENGINEERING || editContext === PERMIT_EDIT_CONTEXT.PERMITTING
  );
}

/** Whether riders can be added/removed in the current tab context. */
export function canManagePermitRiders(editContext) {
  return (
    editContext === PERMIT_EDIT_CONTEXT.ENGINEERING || editContext === PERMIT_EDIT_CONTEXT.PERMITTING
  );
}

/** Whether a permit sub-field is editable in the given tab context. */
export function permitSubFieldDisabled(field, editContext) {
  const ctxs = field?.editContexts;
  if (!Array.isArray(ctxs) || ctxs.length === 0) return false;
  return !ctxs.includes(editContext);
}

/** Extension riders do not require an engineering permit plan. */
export function riderNeedsPermitPlan(riderType) {
  const t = riderType != null ? String(riderType).trim() : "";
  return t !== "Extension";
}

/** Rider sub-fields visible in a given edit context (and rider row state). */
export function permitRiderFieldVisible(field, editContext, riderRow) {
  if (permitSubFieldDisabled(field, editContext)) return false;
  if (field?.key === "permit_plan" && !riderNeedsPermitPlan(riderRow?.rider_type)) return false;
  return true;
}

function formatMaterialsSummary(materialsRaw) {
  const rows = repeaterToArray(materialsRaw);
  if (!rows.length) return "";
  return rows
    .map((r) => {
      const item = r?.item != null ? String(r.item).trim() : "";
      const slug = r?.slug != null ? String(r.slug).trim() : "";
      const label = item || slug;
      const qty = r?.quantity != null ? String(r.quantity).trim() : "";
      if (!label && !qty) return "";
      return qty ? `${label} (${qty})` : label;
    })
    .filter(Boolean)
    .join("; ");
}

/** Coerce stored ACF / JSON values into a boolean for refundable deposit. */
export function isPermitRefundableDeposit(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null || value === "") return false;
  const s = String(value).trim().toLowerCase();
  return ["1", "true", "yes", "y"].includes(s);
}

/** Board-grid summary: Yes if any permit has a refundable deposit, No if permits exist, else blank. */
export function formatPermitsRefundableDeposit(permitsRaw) {
  const permits = repeaterToArray(permitsRaw);
  if (!permits.length) return "";
  return permits.some((p) => isPermitRefundableDeposit(p?.refundable_deposit)) ? "Yes" : "No";
}

/** Parse a stored deposit amount into a finite number, or null. */
export function parsePermitDepositAmount(value) {
  if (value == null || value === "") return null;
  const n = Number(String(value).replace(/[$,]/g, "").trim());
  return Number.isFinite(n) ? n : null;
}

/** Format a single permit deposit amount for display. */
export function formatPermitDepositAmount(value) {
  const n = parsePermitDepositAmount(value);
  if (n == null) return "";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

/** Board-grid summary: sum of deposit amounts across permits (blank if none). */
export function formatPermitsDepositAmount(permitsRaw) {
  const permits = repeaterToArray(permitsRaw);
  if (!permits.length) return "";
  let total = 0;
  let hasAmount = false;
  for (const p of permits) {
    const n = parsePermitDepositAmount(p?.deposit_amount);
    if (n == null) continue;
    total += n;
    hasAmount = true;
  }
  if (!hasAmount) return "";
  return total.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

function normalizeDateForSort(value) {
  return normalizeDateOnlyIso(value);
}

function formatDateDisplay(value) {
  if (value == null || String(value).trim() === "") return "";
  return formatDateOnlyDisplay(value) || String(value).trim();
}

function permitFileUrl(value) {
  return fileUrlFromStoredValue(value);
}

/** Whether a permit row has an application on file or a submitted date. */
export function permitApplicationSubmitted(permitRow) {
  if (!permitRow || typeof permitRow !== "object") return false;
  if (permitFileUrl(permitRow.permit_application)) return true;
  const d = permitRow.application_submitted_date;
  return d != null && String(d).trim() !== "";
}

/** Count permits with applications submitted vs total permit rows. */
export function countPermitApplications(permitsRaw) {
  const permits = repeaterToArray(permitsRaw);
  let applied = 0;
  for (const p of permits) {
    if (permitApplicationSubmitted(p)) applied += 1;
  }
  return { applied, total: permits.length };
}

/** Whether a permit row has an issued permit on file or a received date. */
export function permitIssued(permitRow) {
  if (!permitRow || typeof permitRow !== "object") return false;
  if (permitFileUrl(permitRow.permit)) return true;
  const d = permitRow.permit_received_date;
  return d != null && String(d).trim() !== "";
}

/** Count permits issued vs total permit rows. */
export function countPermitIssued(permitsRaw) {
  const permits = repeaterToArray(permitsRaw);
  let issued = 0;
  for (const p of permits) {
    if (permitIssued(p)) issued += 1;
  }
  return { issued, total: permits.length };
}

/** Flatten permits (+ riders) into AG Grid row objects. */
export function flattenPermitsForGrid(permitsRaw) {
  const permits = repeaterToArray(permitsRaw);
  const rows = [];
  for (let pi = 0; pi < permits.length; pi += 1) {
    const p = permits[pi];
    rows.push({
      rowId: `p-${pi}`,
      rowKind: "Permit",
      permitIndex: pi,
      riderIndex: null,
      rider_type: null,
      agency: p?.agency != null ? String(p.agency).trim() : "",
      location: p?.location != null ? String(p.location).trim() : "",
      line: `Permit ${pi + 1}`,
      permit_number: p?.permit_number != null ? String(p.permit_number).trim() : "",
      refundable_deposit: isPermitRefundableDeposit(p?.refundable_deposit),
      deposit_amount: formatPermitDepositAmount(p?.deposit_amount),
      application_submitted_date: formatDateDisplay(p?.application_submitted_date),
      permit_received_date: formatDateDisplay(p?.permit_received_date),
      permit_expiration_date: formatDateDisplay(p?.permit_expiration_date),
      application_submitted_sort: normalizeDateForSort(p?.application_submitted_date),
      permit_received_sort: normalizeDateForSort(p?.permit_received_date),
      permit_expiration_sort: normalizeDateForSort(p?.permit_expiration_date),
      permit_plan: permitFileUrl(p?.permit_plan),
      permit_application: permitFileUrl(p?.permit_application),
      permit: permitFileUrl(p?.permit),
      materials: formatMaterialsSummary(p?.materials),
    });
    const riders = repeaterToArray(p?.riders);
    for (let ri = 0; ri < riders.length; ri += 1) {
      const r = riders[ri];
      const type = r?.rider_type != null ? String(r.rider_type).trim() : "Supporting document";
      rows.push({
        rowId: `p-${pi}-r-${ri}`,
        rowKind: "Supporting doc",
        permitIndex: pi,
        riderIndex: ri,
        rider_type: type,
        agency: p?.agency != null ? String(p.agency).trim() : "",
        location: p?.location != null ? String(p.location).trim() : "",
        line: `↳ ${type}${riders.length > 1 ? ` ${ri + 1}` : ""}`,
        permit_number: r?.permit_number != null ? String(r.permit_number).trim() : "",
        refundable_deposit: false,
        deposit_amount: "",
        application_submitted_date: formatDateDisplay(r?.application_submitted_date),
        permit_received_date: formatDateDisplay(r?.permit_received_date),
        permit_expiration_date: formatDateDisplay(r?.permit_expiration_date),
        application_submitted_sort: normalizeDateForSort(r?.application_submitted_date),
        permit_received_sort: normalizeDateForSort(r?.permit_received_date),
        permit_expiration_sort: normalizeDateForSort(r?.permit_expiration_date),
        permit_plan: permitFileUrl(r?.permit_plan),
        permit_application: permitFileUrl(r?.permit_application),
        permit: permitFileUrl(r?.permit),
        materials: "",
      });
    }
  }
  return rows;
}

function pushConstructionPermitFile(items, label, value, fieldKey) {
  if (!fileUrlFromStoredValue(value)) return;
  items.push({ label, value, fieldKey, accept: ".pdf" });
}

/** Group permit and rider plan/permit files for the Construction tab. */
export function collectConstructionPermitFileSections(permitsRaw) {
  const permits = repeaterToArray(permitsRaw);
  const sections = [];
  for (let pi = 0; pi < permits.length; pi += 1) {
    const p = permits[pi];
    const agency = p?.agency != null ? String(p.agency).trim() : "";
    const title = `Permit ${pi + 1}${agency ? ` — ${agency}` : ""}`;
    const files = [];
    pushConstructionPermitFile(files, "Permit plan", p?.permit_plan, "permit_plan");
    pushConstructionPermitFile(files, "Permit", p?.permit, "permit");

    const riders = [];
    const riderRows = repeaterToArray(p?.riders);
    for (let ri = 0; ri < riderRows.length; ri += 1) {
      const r = riderRows[ri];
      const type = r?.rider_type != null ? String(r.rider_type).trim() : "Supporting document";
      const riderTitle = `${type}${riderRows.length > 1 ? ` ${ri + 1}` : ""}`;
      const riderFiles = [];
      pushConstructionPermitFile(riderFiles, "Supporting document", r?.permit_plan, "permit_plan");
      pushConstructionPermitFile(riderFiles, "Permit", r?.permit, "permit");
      if (riderFiles.length) riders.push({ title: riderTitle, files: riderFiles });
    }

    if (files.length || riders.length) {
      sections.push({ permitIndex: pi, title, files, riders });
    }
  }
  return sections;
}

function isExtensionRiderRow(row) {
  return (
    row?.rowKind === "Supporting doc" &&
    String(row?.rider_type || "").trim() === "Extension"
  );
}

function isPrimaryPermitRow(row) {
  return row?.rowKind === "Permit";
}

/**
 * Aggregate permit dates across permits (and optionally supporting-document riders)
 * for main project grids and reports.
 *
 * @param {object} [options]
 * @param {boolean} [options.includeSupportingDocuments=true] When false, ignore
 *   rider/supporting-doc dates (extensions, amendments, TCP, etc.).
 */
export function aggregatePermitDates(permitsRaw, options = {}) {
  const includeSupportingDocuments = options.includeSupportingDocuments !== false;
  const flat = flattenPermitsForGrid(permitsRaw).filter((row) =>
    includeSupportingDocuments ? true : isPrimaryPermitRow(row)
  );
  const receivedRows = flat
    .filter((r) => r.permit_received_sort)
    .sort((a, b) => String(a.permit_received_sort).localeCompare(String(b.permit_received_sort)));
  const latestReceivedSort = receivedRows.length
    ? receivedRows[receivedRows.length - 1].permit_received_sort
    : "";
  const receivedAtLatest = latestReceivedSort
    ? receivedRows.filter((r) => r.permit_received_sort === latestReceivedSort)
    : [];
  /** True when the latest received date comes only from Extension rider(s). */
  const permit_received_is_extension =
    includeSupportingDocuments &&
    receivedAtLatest.length > 0 &&
    receivedAtLatest.every(isExtensionRiderRow);

  const expiration = flat.map((r) => r.permit_expiration_sort).filter(Boolean).sort();
  const submitted = flat.map((r) => r.application_submitted_sort).filter(Boolean).sort();
  return {
    permit_received_date: latestReceivedSort || "",
    permit_received_is_extension,
    permit_expiration_date: expiration.length ? expiration[expiration.length - 1] : "",
    application_submitted_date: submitted.length ? submitted[submitted.length - 1] : "",
  };
}

/** First permit file URL for legacy permit_files grid column. */
export function permitsFileGridValue(permitsRaw, legacyPermitFiles) {
  if (legacyPermitFiles != null && String(legacyPermitFiles).trim() !== "") {
    return legacyPermitFiles;
  }
  const permits = repeaterToArray(permitsRaw);
  for (const p of permits) {
    for (const key of ["permit", "permit_application", "permit_plan"]) {
      const v = p?.[key];
      if (typeof v === "string" && v.trim()) return v.trim();
      if (v && typeof v === "object" && v.url) return String(v.url).trim();
    }
    for (const r of repeaterToArray(p?.riders)) {
      for (const key of ["permit", "permit_application", "permit_plan"]) {
        const v = r?.[key];
        if (typeof v === "string" && v.trim()) return v.trim();
        if (v && typeof v === "object" && v.url) return String(v.url).trim();
      }
    }
  }
  return "";
}

/** Read a project-level permitting tracking field from engineering_details. */
export function readPermittingTrackingField(eng, key) {
  if (!eng || typeof eng !== "object") return "";
  const v = eng[key];
  if (v == null) return "";
  return String(v).trim();
}

/**
 * Build PATCH body that sets one engineering_details tracking field from the board grid.
 * @returns {{ acf_groups_json: object, _source: string } | null}
 */
export function buildPermittingTrackingFieldPatch(row, fieldKey, rawValue) {
  if (!PERMITTING_TRACKING_FIELD_KEYS.includes(fieldKey)) return null;
  const field = PERMITTING_TRACKING_FIELDS.find((f) => f.key === fieldKey);
  let nextValue = rawValue == null ? "" : String(rawValue).trim();
  if (field?.type === "date") {
    nextValue = nextValue ? normalizeDateOnlyIso(nextValue) || nextValue : "";
  }
  const ag =
    row?.acf_groups_json != null &&
    typeof row.acf_groups_json === "object" &&
    !Array.isArray(row.acf_groups_json)
      ? JSON.parse(JSON.stringify(row.acf_groups_json))
      : {};
  const engKey = "engineering_details";
  const legacyKeys = ["Engineering Test 2", "engineering_test_2"];
  let targetKey = engKey;
  if (!ag[engKey] || typeof ag[engKey] !== "object" || Array.isArray(ag[engKey])) {
    const legacy = legacyKeys.find((k) => ag[k] && typeof ag[k] === "object" && !Array.isArray(ag[k]));
    if (legacy) targetKey = legacy;
    else ag[engKey] = {};
  }
  if (!ag[targetKey] || typeof ag[targetKey] !== "object" || Array.isArray(ag[targetKey])) {
    ag[targetKey] = {};
  }
  ag[targetKey] = { ...ag[targetKey], [fieldKey]: nextValue };
  if (targetKey !== engKey) {
    if (!ag[engKey] || typeof ag[engKey] !== "object" || Array.isArray(ag[engKey])) {
      ag[engKey] = {};
    }
    ag[engKey] = { ...ag[engKey], [fieldKey]: nextValue };
  }
  return { acf_groups_json: ag, _source: "grid" };
}
